import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
  Table,
} from 'components';
import PropTypes from 'prop-types';
import {
  loadIpmaCisCreator,
  setIpmaCisPageCreator,
  setIpmaCisPerPageCreator,
  setIpmaCisSortCreator,
  setIpmaCisFiltersCreator,
  removeIpmaCisFiltersCreator,
  setIpmaCisProfileCreator,
} from 'store/actionsCreators';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import {
  loadGlobalCisCertificationBodiesAttributesCreator,
  removeIpmaCisCreator,
  toggleCertificationBodyCreator,
} from '../../../../store/actionsCreators';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';

const CertificationBodies = ({
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  removeFilters,
  setProfile,
  removeCertificationBody,
  countries,
  loadCertificationBodiesAttributes,
  toggleSuspended,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const isCvmbCis = history.location?.pathname?.includes('cvmb-cis') ?? false;
  const manageCisPermission =
    (isCvmbCis && checkPermissions({ roles: ['ipma_cis_cvmb'] }, userPermissions)) ||
    (!isCvmbCis && checkPermissions({ roles: ['ipma_cis'] }, userPermissions));

  useEffect(() => {
    if (document.getElementById('ipma-cis-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    loadCertificationBodiesAttributes();
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.country || ''}
                  onFilter={(val) => handleColumnFilterChange('country', val)}
                  filterOptions={countries}
                  filterPlaceholder={t('page.user.cis.certificationBodies.countryPlaceholder')}
                  label={t('page.user.cis.certificationBodies.country')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.initials || ''}
                  onFilter={(val) => handleColumnFilterChange('initials', val)}
                  filterPlaceholder={t('page.user.cis.certificationBodies.initialsPlaceholder')}
                  label={t('page.user.cis.certificationBodies.initials')}
                  sort
                  sortDirection={getSortDirection('initials')}
                  onSort={(dir) => handleColumnSort('initials', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.name || ''}
                  onFilter={(val) => handleColumnFilterChange('name', val)}
                  filterPlaceholder={t('page.user.cis.certificationBodies.namePlaceholder')}
                  label={t('page.user.cis.certificationBodies.name')}
                  sort
                  sortDirection={getSortDirection('name')}
                  onSort={(dir) => handleColumnSort('name', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.cbHeadName || ''}
                  onFilter={(val) => handleColumnFilterChange('cbHeadName', val)}
                  filterPlaceholder={t('page.user.cis.certificationBodies.cbHeadNamePlaceholder')}
                  label={t('page.user.cis.certificationBodies.cbHeadName')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.contactEmail || ''}
                  onFilter={(val) => handleColumnFilterChange('contactEmail', val)}
                  filterPlaceholder={t('page.user.cis.certificationBodies.contactEmailPlaceholder')}
                  label={t('page.user.cis.certificationBodies.contactEmail')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.contactTelephone || ''}
                  onFilter={(val) => handleColumnFilterChange('contactTelephone', val)}
                  filterPlaceholder={t(
                    'page.user.cis.certificationBodies.contactTelephonePlaceholder',
                  )}
                  label={t('page.user.cis.certificationBodies.contactTelephone')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.vatId || ''}
                  onFilter={(val) => handleColumnFilterChange('vatId', val)}
                  filterPlaceholder={t('page.user.cis.certificationBodies.vatIdPlaceholder')}
                  label={t('page.user.cis.certificationBodies.vatId')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.suspended || ''}
                  onFilter={(val) => handleColumnFilterChange('suspended', val)}
                  filterOptions={[
                    { value: true, label: t('global.yes') },
                    { value: false, label: t('global.no') },
                  ]}
                  filterPlaceholder={t('page.user.cis.certificationBodies.suspendedPlaceholder')}
                  label={t('page.user.cis.certificationBodies.suspended')}
                  sort
                  sortDirection={getSortDirection('suspended')}
                  onSort={(dir) => handleColumnSort('suspended', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.user || ''}
                  onFilter={(val) => handleColumnFilterChange('user', val)}
                  filterPlaceholder={t('page.user.cis.certificationBodies.userPlaceholder')}
                  label={t('page.user.cis.certificationBodies.user')}
                />
              </CisTable.StickyHeading>
              {manageCisPermission && (
                <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                  <TableHeaderCell
                    label={t('page.user.cis.certificationBodies.actions')}
                    labelAlign="center"
                    isAction={true}
                  />
                </CisTable.StickyActionHeading>
              )}
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={9}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={9}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationBodies.country')}:</Table.Label>
                      {item.country?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationBodies.initials')}:</Table.Label>
                      {item.initials}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationBodies.name')}:</Table.Label>
                      {item.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.certificationBodies.cbHeadName')}:
                      </Table.Label>
                      {item.cbHeadName}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.certificationBodies.contactEmail')}:
                      </Table.Label>
                      {item.contactEmail}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.certificationBodies.contactTelephone')}:
                      </Table.Label>
                      {item.contactTelephone}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationBodies.vatId')}:</Table.Label>
                      {item.vatId}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationBodies.suspended')}:</Table.Label>
                      {item.suspended ? t('global.yes') : t('global.no')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationBodies.user')}:</Table.Label>
                      {item.user && `${item.user.firstName} ${item.user.lastName}`}
                    </CisTable.Cell>
                    {manageCisPermission && (
                      <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                        <Table.Label>{t('page.user.cis.certificationBodies.actions')}:</Table.Label>
                        <Row style={{ gap: '3px', justifyContent: 'center' }}>
                          {isCvmbCis ? (
                            <ButtonIcon
                              style={{
                                width: '70%',
                              }}
                              secondary
                              onClick={() => {
                                toggleSuspended(item.id);
                              }}
                            >
                              {item.suspended
                                ? t('page.user.cis.certificationBodies.unsuspend')
                                : t('page.user.cis.certificationBodies.suspend')}
                            </ButtonIcon>
                          ) : (
                            <>
                              <ButtonIcon
                                icon="edit"
                                secondary
                                as={Button}
                                onClick={() => {
                                  setProfile(item);
                                  history.push('/ipma/cis/certification-bodies/edit');
                                }}
                              />
                              <ButtonIcon
                                icon="trash"
                                onClick={() => {
                                  setOpenConfirmModal(true);
                                  setSelectedItem(item);
                                }}
                              />
                            </>
                          )}
                        </Row>
                      </CisTable.StickyActionCell>
                    )}
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.user.cis.certificationBodies.confirmDelete')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeCertificationBody(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        manageCisPermission &&
        !isCvmbCis &&
        ReactDOM.createPortal(
          <Button
            style={{ width: '30%' }}
            secondary
            as={Link}
            to="/ipma/cis/certification-bodies/create"
          >
            {t('page.user.cis.certificationBodies.new')}
          </Button>,
          document.getElementById('ipma-cis-buttons-portal'),
        )}
    </PageContent>
  );
};

CertificationBodies.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeCertificationBody: PropTypes.func.isRequired,
};

CertificationBodies.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ipma, globals, user }) => ({
  loading: isLoading(loading, 'LOAD_IPMA_CIS'),
  list: ipma.cis.certificationBodies,
  countries: globals.countries,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadIpmaCisCreator('certificationBodies')),
  setPage: (payload) => dispatch(setIpmaCisPageCreator('certificationBodies', payload)),
  setPerPage: (payload) => dispatch(setIpmaCisPerPageCreator('certificationBodies', payload)),
  setSort: (payload) => dispatch(setIpmaCisSortCreator('certificationBodies', payload)),
  setFilters: (payload) => dispatch(setIpmaCisFiltersCreator('certificationBodies', payload)),
  removeFilters: () => dispatch(removeIpmaCisFiltersCreator('certificationBodies')),
  setProfile: (payload) => dispatch(setIpmaCisProfileCreator('certificationBodies', payload)),
  removeCertificationBody: (payload) =>
    dispatch(removeIpmaCisCreator('certificationBodies', payload)),
  loadCertificationBodiesAttributes: () =>
    dispatch(loadGlobalCisCertificationBodiesAttributesCreator()),
  toggleSuspended: (id) => dispatch(toggleCertificationBodyCreator(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CertificationBodies);
